import { useCallback, useEffect, useState } from "react";
import { Button, EmptyState, Input, SkeletonText, useToast } from "@astrum/ui";
import type { BudgetMember } from "@astrum/shared";
import { api, ApiError } from "../lib/api.js";
import { editHours, editMoney, parseHours, parseMoney } from "../lib/budget-format.js";
import "./project-budget-tab.css";

/**
 * Per-person weekly capacity and hourly cost rate for the active office.
 * Rates are admin-only on the server: this panel is mounted in Admin, and the
 * rate column is shown only when the server sends rates at all.
 */

const PAGE = 25;

type Draft = { capacity: string; rate: string };

export function BudgetRatesAdmin() {
  const { toast } = useToast();
  const [members, setMembers] = useState<BudgetMember[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [saving, setSaving] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ limit: String(PAGE), offset: String(offset) });
      if (q.trim()) qs.set("q", q.trim());
      const res = await api.get<{ members: BudgetMember[]; total: number }>(`/budget/members?${qs}`);
      setMembers(res.members);
      setTotal(res.total);
      setDrafts(
        Object.fromEntries(
          res.members.map((m) => [m.userId, { capacity: editHours(m.weeklyCapacityMinutes), rate: editMoney(m.hourlyCostCents ?? null) }]),
        ),
      );
    } catch (error) {
      toast(error instanceof ApiError ? error.message : "Could not load people.", { tone: "danger" });
    } finally {
      setLoading(false);
    }
  }, [offset, q, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const showRates = members.some((m) => m.hourlyCostCents !== undefined);

  const save = async (m: BudgetMember) => {
    const draft = drafts[m.userId];
    if (!draft) return;
    const capacity = parseHours(draft.capacity);
    const rate = parseMoney(draft.rate);
    if (capacity === undefined || capacity === null || rate === undefined) {
      toast("Enter capacity in hours and the rate as a number such as 45.50.", { tone: "danger" });
      return;
    }
    setSaving(m.userId);
    try {
      await api.patch(`/budget/members/${m.userId}`, { weeklyCapacityMinutes: capacity, ...(showRates ? { hourlyCostCents: rate } : {}) });
      toast("Saved.");
      await load();
    } catch (error) {
      toast(error instanceof ApiError ? error.message : "Could not save.", { tone: "danger" });
    } finally {
      setSaving(null);
    }
  };

  return (
    <section className="admin__panel budget-card" aria-label="Capacity and rates">
      <h3>Capacity and rates</h3>
      <p className="budget-note">Weekly capacity feeds the Insights workload. Hourly cost rates feed project budgets and are visible to admins only.</p>
      <Input label="Search people" value={q} onChange={(e) => { setOffset(0); setQ(e.target.value); }} />
      {loading ? (
        <SkeletonText lines={4} />
      ) : members.length === 0 ? (
        <EmptyState icon="people" title="No people found" description="Try a different search." />
      ) : (
        <table className="budget-table">
          <thead>
            <tr>
              <th>Person</th>
              <th>Weekly capacity (h)</th>
              {showRates && <th>Hourly cost rate</th>}
              <th />
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.userId}>
                <td>{m.displayName ?? m.email}</td>
                <td>
                  <Input
                    aria-label={`Weekly capacity for ${m.displayName ?? m.email}`}
                    inputMode="decimal"
                    value={drafts[m.userId]?.capacity ?? ""}
                    onChange={(e) => setDrafts({ ...drafts, [m.userId]: { ...drafts[m.userId]!, capacity: e.target.value } })}
                  />
                </td>
                {showRates && (
                  <td>
                    <Input
                      aria-label={`Hourly cost rate for ${m.displayName ?? m.email}`}
                      inputMode="decimal"
                      value={drafts[m.userId]?.rate ?? ""}
                      onChange={(e) => setDrafts({ ...drafts, [m.userId]: { ...drafts[m.userId]!, rate: e.target.value } })}
                    />
                  </td>
                )}
                <td>
                  <Button size="sm" variant="secondary" loading={saving === m.userId} onClick={() => void save(m)}>
                    Save
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {total > PAGE && (
        <div className="budget-pager">
          <Button size="sm" variant="ghost" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
            Previous
          </Button>
          <span>
            {offset + 1}-{Math.min(offset + PAGE, total)} of {total}
          </span>
          <Button size="sm" variant="ghost" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>
            Next
          </Button>
        </div>
      )}
    </section>
  );
}
